
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArtItem, Language } from '../types';

interface HeroCarouselProps {
  arts: ArtItem[];
  language: Language;
  onSelectArt?: (art: ArtItem) => void;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ arts, language, onSelectArt }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (arts.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % arts.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [arts]);

  if (arts.length === 0) return null;

  const isVideo = (url?: string) => {
    if (!url) return false;
    return url.toLowerCase().includes('.mp4') || url.includes('giphy.mp4');
  };

  const getVisibleItems = () => {
    const len = arts.length;
    if (len === 1) return [{ art: arts[0], position: 'center' }];
    const prevIndex = (index - 1 + len) % len;
    const nextIndex = (index + 1) % len;
    return [
      { art: arts[prevIndex], position: 'left' },
      { art: arts[index], position: 'center' },
      { art: arts[nextIndex], position: 'right' },
    ];
  };

  const activeArt = arts[index] || arts[0];

  return (
    <div className="relative w-full max-w-[1400px] flex flex-col items-center justify-center pt-2 pb-0">
      {/* 3D Rotating Stage - Artwork Images */}
      <div className="relative w-full h-[340px] sm:h-[440px] md:h-[540px] flex items-center justify-center" style={{ perspective: '1200px' }}>
        <AnimatePresence initial={false} mode="popLayout">
          {getVisibleItems().map(({ art, position }) => {
            const isCenter = position === 'center';
            const isLeft = position === 'left';
            const isRight = position === 'right';

            return (
              <motion.div
                key={art.id}
                initial={{ 
                  opacity: 0,
                  scale: 0.6,
                  x: isLeft ? -500 : isRight ? 500 : 0,
                  rotateY: isLeft ? 45 : isRight ? -45 : 0,
                  zIndex: 0
                }}
                animate={{
                  opacity: isCenter ? 1 : 0.35,
                  scale: isCenter ? 1 : 0.72,
                  x: isCenter ? 0 : isLeft ? -380 : 380,
                  rotateY: isCenter ? 0 : isLeft ? 35 : -35,
                  zIndex: isCenter ? 30 : 10,
                }}
                exit={{ 
                  opacity: 0,
                  scale: 0.6,
                  x: isLeft ? -500 : isRight ? 500 : 0,
                  zIndex: 0
                }}
                transition={{
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1]
                }}
                onClick={() => {
                  if (isLeft) setIndex((index - 1 + arts.length) % arts.length);
                  else if (isRight) setIndex((index + 1) % arts.length);
                  else if (isCenter) onSelectArt?.(art);
                }}
                className={`absolute w-[300px] md:w-[500px] h-full flex items-center justify-center select-none pointer-events-auto cursor-pointer ${
                  isCenter ? 'group' : 'hover:opacity-60 transition-opacity'
                }`}
                title={isCenter ? (language === 'pt' ? 'Clique para ver na galeria' : 'Click to view in gallery') : (art.title[language] || art.title.en || art.title.pt)}
              >
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className={`relative w-full h-full flex items-center justify-center transition-all duration-700 ${isCenter ? '' : 'brightness-50 grayscale-[1]'}`}>
                    <div className="relative w-full h-full flex items-center justify-center">
                      {isVideo(art.videoUrl || art.imageUrl) ? (
                        <video
                          src={art.videoUrl || art.imageUrl}
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="auto"
                          className={`max-w-full max-h-full object-contain transition-transform duration-500 ${isCenter ? 'drop-shadow-[0_25px_50px_rgba(0,0,0,0.9)] group-hover:scale-[1.02]' : ''}`}
                        />
                      ) : (
                        <img
                          src={art.imageUrl}
                          alt={art.title[language] || art.title.en || art.title.pt}
                          className={`max-w-full max-h-full object-contain transition-transform duration-500 ${isCenter ? 'drop-shadow-[0_25px_50px_rgba(0,0,0,0.9)] group-hover:scale-[1.02]' : ''}`}
                        />
                      )}

                      {/* Floating tooltip on hover over center art */}
                      {isCenter && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-black/85 backdrop-blur-md border border-[#c084fc]/60 text-white text-[9px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none rounded-full shadow-[0_4px_20px_rgba(192,132,252,0.3)] flex items-center gap-2 whitespace-nowrap z-30">
                          <span className="text-[#c084fc] font-bold">★</span>
                          <span>{language === 'pt' ? 'Ver na Galeria' : 'View in Gallery'}</span>
                          <span className="text-[#c084fc]">→</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Artwork Name STRICTLY BELOW the image - closely spaced and clickable */}
      {activeArt && (
        <div className="w-full max-w-3xl flex flex-col items-center justify-center mt-2 md:mt-3 px-4 z-20">
          <AnimatePresence mode="wait">
            <motion.button 
              key={activeArt.id}
              type="button"
              onClick={() => onSelectArt?.(activeArt)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="flex flex-col items-center text-center space-y-0.5 group cursor-pointer focus:outline-none"
              title={language === 'pt' ? 'Clique para ver na galeria' : 'Click to view in gallery'}
            >
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-[#c084fc] group-hover:text-white uppercase tracking-wider leading-snug carousel-art-title text-center transition-colors">
                {activeArt.title[language] || activeArt.title.en || activeArt.title.pt}
              </h1>
              {activeArt.subcategory && (
                <span className="text-[10px] md:text-xs text-[#c084fc] font-bold uppercase tracking-[0.25em] opacity-90 mt-1 group-hover:text-white transition-colors">
                  {activeArt.subcategory}
                </span>
              )}
            </motion.button>
          </AnimatePresence>
        </div>
      )}

      {/* Slide indicators */}
      <div className="mt-2 flex gap-2.5 z-30">
        {arts.map((_, i) => (
          <button 
            key={i} 
            onClick={() => setIndex(i)} 
            className="group py-2 px-1 cursor-pointer focus:outline-none"
            aria-label={`Arte ${i + 1}`}
          >
            <div className={`h-[2px] transition-all duration-500 rounded-full ${i === index ? 'bg-[#c084fc] w-10' : 'bg-white/20 w-5 group-hover:bg-white/40'}`} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;
